/* Public trial application. Nothing is granted here — it lands in the owner's
   Pending signups queue and the plant only exists once he approves it. */
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle2, ChevronRight, ArrowLeft, ShieldCheck, Clock } from "lucide-react";
import { grantAccess } from "@/lib/accessKey";

const CONTACT = "admin@armorbatch.com";

export default function Signup() {
  const nav = useNavigate();
  const [f, setF] = useState({ company: "", name: "", email: "", phone: "", country: "", trucks: 3, password: "", confirm: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const quote = useMemo(() => Math.max(1, Number(f.trucks) || 1) * 100, [f.trucks]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.company.trim() || !f.name.trim() || !f.email.trim() || !f.phone.trim()) {
      toast.error("Company, your name, email and phone are all needed");
      return;
    }
    if (f.password.length < 8 || !/[A-Za-z]/.test(f.password) || !/\d/.test(f.password)) {
      toast.error("Password needs at least 8 characters including a letter and a number");
      return;
    }
    if (f.password !== f.confirm) {
      toast.error("Those passwords don't match");
      return;
    }
    setBusy(true);
    try {
      const { confirm, ...body } = f;
      await api.post("/public/signup", { ...body, trucks: Number(f.trucks) || 1 });
      grantAccess();   // a trial applicant needs the login screen once they're approved
      setSent(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't send that — please email us instead");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6 py-12" data-testid="signup-sent">
        <div className="max-w-xl">
          <CheckCircle2 className="w-10 h-10 text-emerald-600" />
          <h1 className="mt-5 text-3xl sm:text-4xl font-black tracking-tight text-zinc-900">Application received.</h1>
          <p className="mt-4 text-base text-zinc-600">
            We check every application by hand — usually within one business day. You'll get an email
            the moment <b>{f.company}</b> is approved, and you can sign straight in with the password you just chose.
            Your 14-day trial starts then, not now, so none of it is wasted waiting on us.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/tour" data-testid="signup-sent-tour"
              className="inline-flex items-center gap-2 bg-[#1D4ED8] hover:bg-[#1A43BA] text-white text-sm font-bold uppercase tracking-widest px-6 py-3.5 transition-colors">
              Keep looking around <ChevronRight className="w-4 h-4" />
            </Link>
            <a href={`mailto:${CONTACT}`} className="inline-flex items-center gap-2 border border-zinc-300 hover:border-zinc-900 text-zinc-800 text-sm font-bold uppercase tracking-widest px-6 py-3.5 transition-colors">
              Email us
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0C0E] text-zinc-200" data-testid="signup-page">
      <div className="border-b border-zinc-800 px-5 py-4 flex items-center gap-3">
        <button type="button" onClick={() => nav("/tour")} data-testid="signup-back"
          className="inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <div className="mono text-[10px] tracking-[0.4em] uppercase text-zinc-400 ml-2">ArmorBatch Global</div>
        <Link to="/login" data-testid="signup-signin"
          className="ml-auto mono text-[9px] uppercase tracking-[0.3em] text-zinc-500 hover:text-white">Sign in</Link>
      </div>

      <div className="max-w-5xl mx-auto px-5 py-12 grid lg:grid-cols-[1.1fr_1fr] gap-12">
        <div>
          <div className="mono text-[10px] tracking-[0.4em] uppercase text-[#F57C00]">14-day trial</div>
          <h1 className="mt-4 text-4xl sm:text-5xl font-black tracking-tight leading-[1.05] text-white">
            Start your plant.
          </h1>
          <p className="mt-5 text-base text-zinc-400">
            Tell us about your operation. We approve each plant by hand — no card, no commitment,
            and your trial clock only starts once you're in.
          </p>
          <ul className="mt-8 space-y-3">
            {[
              [ShieldCheck, "Approved by a human", "We set you up properly rather than dumping you in an empty system."],
              [Clock, "14 days, from the day you're approved", "Waiting on us doesn't eat into your trial."],
              [CheckCircle2, "A plant that already works", "Sample customers, trucks, mixes and today's jobs, with one button to clear them when you go live."],
            ].map(([Icon, t, d]) => (
              <li key={t} className="flex gap-3">
                <Icon className="w-4 h-4 text-[#F57C00] mt-1 shrink-0" />
                <div>
                  <div className="text-sm font-bold text-zinc-100">{t}</div>
                  <div className="text-sm text-zinc-500">{d}</div>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-8 border border-zinc-800 bg-[#101215] px-5 py-4">
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Your estimate</div>
            <div className="text-2xl font-black text-white mt-1" data-testid="signup-quote">
              ${quote}<span className="text-sm font-bold text-zinc-500"> USD / month</span>
            </div>
            <div className="mono text-[10px] uppercase tracking-widest text-zinc-600 mt-1">
              {Math.max(1, Number(f.trucks) || 1)} trucks × $100 · after the trial
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="border border-zinc-800 bg-[#101215] p-5" data-testid="signup-form">
          <div className="grid gap-2.5">
            {[
              ["company", "Company / plant name *", "text"],
              ["name", "Your name *", "text"],
              ["email", "Work email *", "email"],
              ["phone", "Phone (with country code) *", "tel"],
              ["country", "Country", "text"],
            ].map(([k, ph, type]) => (
              <Input key={k} type={type} value={f[k]} onChange={set(k)} placeholder={ph}
                data-testid={`signup-${k}`} autoComplete="off"
                className="rounded-none bg-transparent border-zinc-700 h-11 text-base text-zinc-100 placeholder:text-zinc-600" />
            ))}
            <div>
              <div className="mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">How many trucks?</div>
              <Input type="number" min={1} max={500} value={f.trucks} onChange={set("trucks")} data-testid="signup-trucks"
                className="rounded-none bg-transparent border-zinc-700 h-11 text-base text-zinc-100" />
            </div>
            <Input type="password" value={f.password} onChange={set("password")} placeholder="Choose a password *"
              data-testid="signup-password" autoComplete="new-password"
              className="rounded-none bg-transparent border-zinc-700 h-11 text-base text-zinc-100 placeholder:text-zinc-600" />
            <Input type="password" value={f.confirm} onChange={set("confirm")} placeholder="Confirm password *"
              data-testid="signup-confirm" autoComplete="new-password"
              className="rounded-none bg-transparent border-zinc-700 h-11 text-base text-zinc-100 placeholder:text-zinc-600" />
            <div className="mono text-[9px] uppercase tracking-widest text-zinc-600">
              8+ characters, with at least one letter and one number
            </div>
            <Input value={f.notes} onChange={set("notes")} placeholder="Anything we should know? (optional)"
              data-testid="signup-notes"
              className="rounded-none bg-transparent border-zinc-700 h-11 text-base text-zinc-100 placeholder:text-zinc-600" />
          </div>
          <button type="submit" disabled={busy} data-testid="signup-submit"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#F57C00] hover:bg-[#ff8f1f] disabled:opacity-50 text-black text-sm font-black uppercase tracking-widest px-7 py-4 transition-colors">
            {busy ? "Sending…" : "Apply for my trial"}
          </button>
          <div className="mono text-[9px] uppercase tracking-widest text-zinc-600 mt-3 text-center">
            No card required · we reply within one business day
          </div>
        </form>
      </div>
    </div>
  );
}
